import {
  FC,
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobile, isMobileOnly } from 'react-device-detect';
import { Link, useNavigate } from 'react-router-dom';

import UserIconImg from '@/assets/icons/icon_mymenu.svg';
import { useUserContext } from '@/contexts/user';
import { Wallet } from '@/lib/apollo/types/gen/graphql';

import NonMember from './NonMember';
import {
  BalanceWrapper,
  DrawerButton,
  DrawerIcon,
  ListItem,
  ListWrapper,
  NavWrapper,
  NonMemberWrapper,
  PcBox,
  RightBox,
  SpBox,
  TotalBalance,
  UserIcon,
  WalletMark,
  WalletToggleIcon,
  WalletWrapper,
} from './style';
import WalletInfo, { BalanceType } from './WalletInfo';

const Menu = lazy(() => import('./Menu'));

const Wallets: FC<{
  handleOpenQuickChargeModal: () => void;
}> = ({ handleOpenQuickChargeModal }) => {
  const navigate = useNavigate();
  const wallet = useUserContext().user.wallet as Wallet;

  return (
    <>
      <WalletInfo
        balanceType={BalanceType.Cash}
        balance={wallet.balance}
        handleClick={() => navigate('/wallet')}
      />
      <WalletInfo
        balanceType={BalanceType.Toreta}
        balance={wallet.toreta.balance}
        handleClick={() => handleOpenQuickChargeModal()}
      />
    </>
  );
};

interface NavProps {
  handleOpenQuickChargeModal: () => void;
  isOpenDrawer: boolean;
  setIsOpenDrawer: (bool: boolean) => void;
}

const Nav: FC<NavProps> = ({
  handleOpenQuickChargeModal,
  isOpenDrawer,
  setIsOpenDrawer,
}) => {
  const { user, isMember, isGuest } = useUserContext();
  const [showWalletInfo, setShowWalletInfo] = useState<boolean>(isMobile);
  const ref = useRef<HTMLElement>(null);

  const closeMenu = useCallback(() => {
    setIsOpenDrawer(false);
  }, []);

  const totalBalance = useMemo(() => {
    return user.wallet ? user.wallet.balance + user.wallet.toreta.balance : 0;
  }, [user.wallet?.balance, user.wallet?.toreta.balance]);

  return (
    <>
      <NavWrapper ref={ref}>
        <ListWrapper>
          {isMember ? (
            <>
              <ListItem>
                <Link to="/racecard">出走表</Link>
              </ListItem>
              <ListItem>
                <Link to="/bets/history">投票履歴</Link>
              </ListItem>
            </>
          ) : (
            <ListItem>
              <Link to="/guide">はじめての方へ</Link>
            </ListItem>
          )}
          {!isGuest && (
            <ListItem>
              <Link to="/current_campaign">キャンペーン</Link>
            </ListItem>
          )}
        </ListWrapper>
        <RightBox>
          {isMember ? (
            <>
              <PcBox data-testid="wallet">
                <Wallets
                  handleOpenQuickChargeModal={() =>
                    handleOpenQuickChargeModal()
                  }
                />
              </PcBox>
              <Link to="/my/dashboard" data-testid="mypage-link">
                <UserIcon src={UserIconImg} />
              </Link>
            </>
          ) : (
            <NonMemberWrapper data-testid="non-member-info">
              <NonMember />
            </NonMemberWrapper>
          )}
          {!isMobileOnly && (
            <DrawerButton
              type="button"
              onClick={() => setIsOpenDrawer(!isOpenDrawer)}
              data-testid="drawer-button"
              aria-label={`${isOpenDrawer ? 'close' : 'open'}-menu-button`}
              aria-expanded={isOpenDrawer}
              aria-haspopup="true"
              aria-controls="dropdown-menu"
            >
              <DrawerIcon isOpen={isOpenDrawer} />
            </DrawerButton>
          )}
        </RightBox>
      </NavWrapper>
      {isMember && (
        <SpBox>
          <WalletWrapper data-testid="wallet-sp" isOpen={showWalletInfo}>
            <Wallets
              handleOpenQuickChargeModal={() => handleOpenQuickChargeModal()}
            />
          </WalletWrapper>
          <BalanceWrapper onClick={() => setShowWalletInfo(prev => !prev)}>
            <WalletToggleIcon
              data-testid="wallet-toggle-icon"
              isOpen={showWalletInfo}
            />
            <TotalBalance data-testid="total-balance">
              <WalletMark />
              {totalBalance.toLocaleString()}円
            </TotalBalance>
          </BalanceWrapper>
        </SpBox>
      )}
      <Suspense fallback={<></>}>
        <Menu
          isActive={isOpenDrawer}
          closeMenu={closeMenu}
          aria-labelledby="dropdown-menu"
          aria-hidden={!isOpenDrawer}
        />
      </Suspense>
    </>
  );
};

export default Nav;
